// Phase 8 port of rewise-ui's Phase 7 examPrepService.ts. Named
// examPrepOrchestrationService.ts (not examPrepService.ts) to avoid colliding
// with this app's existing, unrelated, confirmed-dead static content-browser
// service of that name (ExamPrepHome/Subjects/Chapters/Modules) -- left
// completely untouched per product decision.
//
// Service layer for the Exam Prep orchestration page — the checklist RPCs
// (10056_exam_prep_checklist.sql, already shared/live) and the Quick
// Revision / Cheat Sheet content-gen edge function (generate-exam-prep-
// content, already shared/live, grade/board-agnostic via concept_table).

import { supabase } from './supabaseClient';

export type ExamPrepIntent = 'test_tomorrow' | 'term_exam' | 'board_exam';
export type ChecklistItemKey = 'practice_sheet' | 'real_exam_questions' | 'quick_revision' | 'simulation';

export interface ChecklistItem {
  item_key: ChecklistItemKey;
  completed: boolean;
  completed_at: string | null;
}

export async function getExamPrepChecklist(
  studentId: string,
  subjectName: string,
  intent: ExamPrepIntent
): Promise<ChecklistItem[]> {
  const { data, error } = await supabase.rpc('get_exam_prep_checklist', {
    p_student_id: studentId,
    p_subject_name: subjectName,
    p_intent: intent,
  });
  if (error) {
    console.error('Error loading exam prep checklist:', error);
    return [];
  }
  return data || [];
}

export async function toggleExamPrepChecklistItem(
  studentId: string,
  subjectName: string,
  intent: ExamPrepIntent,
  itemKey: ChecklistItemKey,
  completed: boolean
): Promise<void> {
  const { error } = await supabase.rpc('toggle_exam_prep_checklist_item', {
    p_student_id: studentId,
    p_subject_name: subjectName,
    p_intent: intent,
    p_item_key: itemKey,
    p_completed: completed,
  });
  if (error) console.error('Error toggling exam prep checklist item:', error);
}

export type ExamPrepContentType = 'quick_revision' | 'cheat_sheet';

export interface QuickRevisionContent {
  bullets: string[];
}

export interface CheatSheetContent {
  items: { label: string; value: string }[];
}

export interface GenerateExamPrepContentResult {
  content_json?: QuickRevisionContent | CheatSheetContent;
  from_cache?: boolean;
  status?: 'generating';
  error?: string;
}

export async function generateExamPrepContent(params: {
  conceptId: number;
  conceptName: string;
  subject: string;
  gradeBand: '6-8' | '9-10' | '11-12';
  contentType: ExamPrepContentType;
  conceptTable?: 'concepts' | 'concepts_plus1';
}): Promise<GenerateExamPrepContentResult> {
  const { data, error } = await supabase.functions.invoke('generate-exam-prep-content', {
    body: {
      concept_id: params.conceptId,
      concept_name: params.conceptName,
      subject: params.subject,
      grade_band: params.gradeBand,
      concept_table: params.conceptTable ?? 'concepts_plus1',
      content_type: params.contentType,
    },
  });
  if (error) {
    console.error('Error generating exam prep content:', error);
    return { error: 'Something went wrong — try again.' };
  }
  return data as GenerateExamPrepContentResult;
}

export function gradeBandFor(gradeLevel: number): '6-8' | '9-10' | '11-12' {
  if (gradeLevel <= 8) return '6-8';
  if (gradeLevel <= 10) return '9-10';
  return '11-12';
}
